import React, {useState} from 'react';
import moment from 'moment';
import {Link} from 'react-router-dom';
import CircularProgress from '@material-ui/core/CircularProgress';
import {useTranslation} from 'react-i18next';
import panelStyles from '../panelStyles';
import anchorRect from '../tooltipPosition';

const MONTHS_SHOWN = 3;

/**
 * Oda ve musaitlik — ay takvimi.
 * Veri kontenjan ucundan (channel_id = 1, Booking Engine) geliyor; cekilen
 * aralik bugunden 90 gun ileri, yani bugunun ayi dahil uc ay ciziliyor.
 *
 * Hucre yalnizca istisnayi yaziyor: tum odalar aciksa gun numarasi yeter,
 * kismen kapaliysa "2/4" gorunur. Boylece goz sorunlu gunlere kayiyor ve
 * durum yalnizca renge birakilmis olmuyor.
 */
export default function AvailabilityCard({availability, fetching, inventoryUrl}) {
    const {t} = useTranslation();
    const classes = panelStyles();
    // Ipucu ekrana sabit konumlanir: kart overflow:hidden oldugu icin
    // hucre icine yerlestirilen mutlak bir kutu kirpilirdi.
    const [tip, setTip] = useState(null);

    const showTip = (event, info, date) => {
        const box = anchorRect(event.currentTarget);
        setTip({x: box.centerX, y: box.top, limit: box.viewportWidth, date, info});
    };

    // Sayinin neyi saydigi etiketten anlasilmiyor; aciklama ipucta duruyor.
    const showHint = (event, title, body) => {
        const box = anchorRect(event.currentTarget);
        setTip({x: box.centerX, y: box.top, limit: box.viewportWidth, title, body});
    };

    if (fetching) {
        return (
            <section className={classes.card}>
                <div className={classes.loadingBox}>
                    <CircularProgress size={26} style={{color: '#d71f26'}} />
                </div>
            </section>
        );
    }

    if (!availability) {
        return (
            <section className={classes.card}>
                <header className={classes.cardHead}>
                    <div>
                        <h2 className={classes.cardTitle}>{t('enw-panel-availability_title', 'Oda ve Müsaitlik')}</h2>
                    </div>
                </header>
                <div className={classes.emptyBox}>
                    {t('enw-panel-availability_empty', 'Kontenjan bilgisi alınamadı.')}
                    {' '}
                    <Link to={inventoryUrl} style={{color: '#d71f26', fontWeight: 600}}>
                        {t('enw-panel-inventory_calendar', 'Kontenjan takvimi')}
                    </Link>
                </div>
            </section>
        );
    }

    // Gun -> durum eslemesi: takvim hucreleri bunun uzerinden okunuyor
    const byDate = new Map(availability.byDate.map((day) => [day.date, day]));
    const today = moment().startOf('day');

    // Haftanin ilk gunu yerel ayara gore (tr'de Pazartesi)
    const weekStart = moment.localeData().firstDayOfWeek();
    const weekdayLabels = Array.from({length: 7}, (_, i) => moment.weekdaysMin()[(i + weekStart) % 7]);

    const months = Array.from({length: MONTHS_SHOWN}, (_, i) => moment().startOf('month').add(i, 'months'));

    const renderMonth = (monthStart) => {
        const days = monthStart.daysInMonth();
        const offset = (monthStart.day() - weekStart + 7) % 7;
        const cells = [];

        for (let i = 0; i < offset; i += 1) {
            cells.push(<span key={`pad-${i}`} className={classes.calPad} />);
        }

        for (let d = 1; d <= days; d += 1) {
            const date = monthStart.clone().date(d);
            const key = date.format('YYYY-MM-DD');
            const info = byDate.get(key);
            const isToday = date.isSame(today, 'day');
            const isPast = date.isBefore(today, 'day');

            let tone = classes.calNoData;
            let note = null;

            if (info) {
                if (info.open === 0) {
                    tone = classes.calClosed;
                    note = '0';
                } else if (info.open === info.total) {
                    tone = classes.calOpen;
                } else {
                    tone = classes.calPartial;
                    note = `${info.open}/${info.total}`;
                }
            }

            cells.push(
                <span
                    key={key}
                    // Ipucu yalnizca gosterecek verisi olan gunlerde acilir
                    onMouseEnter={info ? (e) => showTip(e, info, date) : undefined}
                    onMouseLeave={info ? () => setTip(null) : undefined}
                    className={[
                        classes.calDay,
                        tone,
                        isPast ? classes.calPast : '',
                        isToday ? classes.calToday : '',
                    ].filter(Boolean).join(' ')}
                >
                    <b>{d}</b>
                    {note && <i>{note}</i>}
                </span>
            );
        }

        return (
            <div key={monthStart.format('YYYY-MM')} className={classes.calMonth}>
                <div className={classes.calMonthName}>{monthStart.format('MMMM YYYY')}</div>
                <div className={classes.calWeekHead}>
                    {weekdayLabels.map((label, i) => <span key={`${label}-${i}`}>{label}</span>)}
                </div>
                <div className={classes.calGrid}>{cells}</div>
            </div>
        );
    };

    return (
        <section className={classes.card}>
            <header className={classes.cardHead}>
                <div>
                    <h2 className={classes.cardTitle}>{t('enw-panel-availability_title', 'Oda ve Müsaitlik')}</h2>
                    <p className={classes.cardDesc}>{t('enw-panel-availability_desc', 'Önümüzdeki 3 ayda hangi günler satışa açık')}</p>
                </div>
                <Link to={inventoryUrl} className={classes.cardNote} style={{color: '#d71f26', fontWeight: 600, textDecoration: 'none'}}>
                    {t('enw-panel-inventory_calendar', 'Kontenjan takvimi')}
                </Link>
            </header>

            <div className={classes.cardBody}>
                <div className={classes.horizon}>
                    <div>
                        <div className={classes.occKey}>{t('enw-panel-active_rooms', 'Bugün satışa açık oda tipi')}</div>
                        <div className={classes.horizonBig}>
                            {availability.activeRooms}
                            <span> / {availability.totalRooms}</span>
                        </div>
                    </div>
                    <div style={{marginLeft: 'auto', textAlign: 'right'}}>
                        {/* Once "bugunden itibaren kesintisiz seri" yaziyordu: yarin tek bir
                            oda tipi kapaliysa 0 gorunuyor, takvimin tamami yesilken otelciye
                            hicbir sey satamiyormus gibi okunuyordu. Artik gun adedi. */}
                        <span
                            className={`${classes.occKey} ${classes.hintAnchor}`}
                            onMouseEnter={(e) => showHint(
                                e,
                                t('enw-panel-sellable_days', 'Satış yapılabilen gün'),
                                [
                                    t('enw-panel-sellable_days_hint', {
                                        defaultValue: 'Önümüzdeki {{total}} günün {{sellable}} gününde en az bir oda tipi satışa açık.',
                                        total: availability.totalDays,
                                        sellable: availability.sellableDays,
                                    }),
                                    availability.closedDays > 0
                                        ? t('enw-panel-closed_days_hint', {
                                            defaultValue: '{{count}} gün hiçbir oda satılamıyor.',
                                            count: availability.closedDays,
                                        })
                                        : null,
                                    availability.partialDays > 0
                                        ? t('enw-panel-partial_days_hint', {
                                            defaultValue: '{{count}} günde oda tiplerinin bir kısmı kapalı — takvimde turuncu görünüyor.',
                                            count: availability.partialDays,
                                        })
                                        : null,
                                ].filter(Boolean).join(' ')
                            )}
                            onMouseLeave={() => setTip(null)}
                        >
                            {t('enw-panel-sellable_days', 'Satış yapılabilen gün')}
                            <i className="fas fa-info-circle" />
                        </span>
                        <div className={classes.horizonBig}>
                            {availability.sellableDays}
                            <span> / {availability.totalDays} {t('enw-panel-days_lower', 'gün')}</span>
                        </div>
                    </div>
                </div>

                {/* Rozetler yalnizca dikkat isteyen gunleri sayar; takvim renkleriyle birebir */}
                <div style={{display: 'flex', gap: 10, marginTop: 14, flexWrap: 'wrap'}}>
                    {availability.partialDays === 0 && availability.closedDays === 0 ? (
                        <span className={`${classes.pill} ${classes.pillOk}`}>
                            <span className={classes.pillDot} />
                            {t('enw-panel-all_days_open', 'Önümüzdeki 3 ayın tamamı satışa açık')}
                        </span>
                    ) : (
                        <>
                            {availability.partialDays > 0 && (
                                <span className={`${classes.pill} ${classes.pillWarn}`}>
                                    <span className={classes.pillDot} />
                                    {t('enw-panel-partial_days', {
                                        defaultValue: '{{count}} günde bazı oda tipleri kapalı',
                                        count: availability.partialDays,
                                    })}
                                </span>
                            )}
                            {availability.closedDays > 0 && (
                                <span className={`${classes.pill} ${classes.pillOff}`}>
                                    <span className={classes.pillDot} />
                                    {t('enw-panel-closed_days', {
                                        defaultValue: '{{count}} gün hiç satış yapılamıyor',
                                        count: availability.closedDays,
                                    })}
                                </span>
                            )}
                        </>
                    )}
                    {availability.closedRooms > 0 && (
                        <span className={`${classes.pill} ${classes.pillWarn}`}>
                            <span className={classes.pillDot} />
                            {t('enw-panel-rooms_closed', {
                                defaultValue: 'Bugün {{count}} oda tipi kapalı',
                                count: availability.closedRooms,
                            })}
                        </span>
                    )}
                </div>

                <div className={classes.divider}>
                    <div className={classes.calMonths}>
                        {months.map(renderMonth)}
                    </div>

                    <div className={classes.legend}>
                        <span className={classes.legendItem}>
                            <b className={classes.legendSwatch} style={{background: '#cdeadb', border: '1px solid #157f4f'}} />
                            {t('enw-panel-legend_all_open', 'Tüm odalar açık')}
                        </span>
                        <span className={classes.legendItem}>
                            <b className={classes.legendSwatch} style={{background: '#fbe6c2', border: '1px solid #a86200'}} />
                            {t('enw-panel-legend_partial', 'Kısmen kapalı')}
                        </span>
                        <span className={classes.legendItem}>
                            <b className={classes.legendSwatch} style={{background: '#f1f4f9', border: '1px solid #c9d2de'}} />
                            {t('enw-panel-legend_closed', 'Tümü kapalı')}
                        </span>
                        <span className={classes.legendItem}>
                            <b className={classes.legendSwatch} style={{background: '#fff', border: '1px dashed #dde3ec'}} />
                            {t('enw-panel-legend_nodata', 'Veri yok')}
                        </span>
                    </div>
                </div>
            </div>

            {tip && (
                <div
                    className={classes.calTip}
                    style={{
                        left: Math.min(Math.max(tip.x, 110), tip.limit - 110),
                        top: tip.y - 10,
                    }}
                >
                    <b>{tip.title || tip.date.format('D MMMM dddd')}</b>
                    {tip.body && <span>{tip.body}</span>}
                    {tip.info && (
                    <span>
                        {tip.info.open === tip.info.total
                            ? t('enw-panel-tip_all_open', 'Tüm oda tipleri açık')
                            : t('enw-panel-tip_open_of', {
                                defaultValue: '{{open}}/{{total}} oda tipi açık',
                                open: tip.info.open,
                                total: tip.info.total,
                            })}
                        {tip.info.units > 0 && ` · ${t('enw-panel-tip_units', {
                            defaultValue: '{{count}} kontenjan',
                            count: tip.info.units,
                        })}`}
                    </span>
                    )}
                    {tip.info && tip.info.closed && tip.info.closed.length > 0 && (
                        <em>
                            {t('enw-panel-tip_closed', 'Kapalı')}: {tip.info.closed.join(', ')}
                        </em>
                    )}
                </div>
            )}
        </section>
    );
}
